import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Settings, Sliders, Trash2, GitCommit, Brain, Cable } from 'lucide-react';
import { Spinner } from '../../design-system';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import { HelpSystem } from '../../components/tour';

// Import section components
import GeneralSection from '../project/GeneralSection';
import DeleteProjectSection from '../project/DeleteProjectSection';
import RevisionSection from '../project/RevisionSection';
import MemoriesSection from '../project/MemoriesSection';
import McpServersSection from '../project/McpServersSection';

// Define sections outside of component for export access
const PROJECT_SECTIONS = [
  { id: 'mcp', label: 'MCP Servers', icon: Cable, component: McpServersSection },
  { id: 'memories', label: 'Memories', icon: Brain, component: MemoriesSection },
  { id: 'revisions', label: 'Revisions', icon: GitCommit, component: RevisionSection },
  { id: 'general', label: 'General', icon: Sliders, component: GeneralSection },
  { id: 'delete', label: 'Delete Project', icon: Trash2, component: DeleteProjectSection },
];

// Export sections IDs for validation
export const getProjectSectionIds = (): string[] => 
  PROJECT_SECTIONS.map(section => section.id);

interface Workspace {
  id: string;
  name: string;
  directory_path: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  deleted?: boolean;
  deleted_at?: string;
  scheduled_hard_delete_at?: string;
  template_slug?: string;
  mcp_servers?: any[]; // Added for MCP support
}

const ProjectSettingsModal: React.FC = () => {
  const api = useAuthenticatedApi();
  const { isProjectSettingsOpen, closeProjectSettings, currentWorkspaceId, initialSection } = useSettingsModal();
  const workspaceId = currentWorkspaceId;

  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSection, setCurrentSection] = useState<string>('environment');
  
  // Set initial section if provided by context
  useEffect(() => {
    if (initialSection && isProjectSettingsOpen) {
      setCurrentSection(initialSection);
    }
  }, [initialSection, isProjectSettingsOpen]);

  // Filter sections based on feature flags and template
  const sections = useMemo(() => {
    return PROJECT_SECTIONS;
  }, []);

  // Ensure current section is valid when sections change (e.g., switching between Expo and non-Expo)
  useEffect(() => {
    if (!loading && sections.length > 0) {
      const currentSectionExists = sections.find(s => s.id === currentSection);
      if (!currentSectionExists) {
        // Reset to first available section if current section is hidden
        setCurrentSection(sections[0].id);
      }
    }
  }, [sections, currentSection, loading]);

  const fetchWorkspace = useCallback(async () => {
    if (!workspaceId) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/workspaces/${workspaceId}`);
      const data = response.data || response;
      setWorkspace(data);
      setError(null);
    } catch (err: any) {
      console.error('Error loading workspace:', err);
      setError(err.message || 'Failed to load workspace');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, api]);

  useEffect(() => {
    if (isProjectSettingsOpen && workspaceId) {
      fetchWorkspace();
    }
  }, [isProjectSettingsOpen, workspaceId, fetchWorkspace]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isProjectSettingsOpen) {
        closeProjectSettings();
      }
    };

    if (isProjectSettingsOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isProjectSettingsOpen, closeProjectSettings]);

  const handleSectionChange = (sectionId: string) => {
    setCurrentSection(sectionId);
  };

  const currentSectionData = sections.find(section => section.id === currentSection);
  const CurrentSectionComponent = currentSectionData?.component || GeneralSection;

  if (!isProjectSettingsOpen) {
    return null;
  }

  // Don't show modal if no workspace ID is available
  if (!workspaceId) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={closeProjectSettings}
    >
      <div 
        className="bg-ui-background-secondary rounded-xl w-full h-full lg:w-[1200px] lg:h-[800px] max-w-[95vw] max-h-[95vh] overflow-hidden flex flex-col shadow-2xl border border-ui-border-primary ring-1 ring-black/5 dark:ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-ui-border-primary bg-neutral-50/50 dark:bg-neutral-800/30">
          <div className="flex items-center">
            <Settings className="w-6 h-6 text-ui-text-secondary mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-ui-text-primary">Project Settings</h2>
              <p className="text-sm text-ui-text-secondary mt-1">Manage your project configuration and settings</p>
            </div>
          </div>
          <button
            onClick={closeProjectSettings}
            className="p-2 hover:bg-ui-background-tertiary rounded-lg transition-colors"
            data-tour="close-settings"
          >
            <X size={24} className="text-ui-text-tertiary" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto lg:overflow-hidden [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-ui-background-tertiary [&::-webkit-scrollbar-track]:dark:bg-ui-background-tertiary [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 [&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
          {/* Error Message */}
          {error && !loading && (
            <div className="m-4 p-4 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-xl">
              <p className="text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          <div className="flex flex-col lg:flex-row min-h-full lg:h-full gap-4 lg:gap-0 p-4 lg:p-0">
            {loading ? (
              <>
                {/* Sidebar placeholder during loading */}
                <div className="lg:w-64 flex-shrink-0 lg:border-r border-b lg:border-b-0 border-ui-border-primary bg-ui-background-tertiary dark:bg-neutral-800/50 rounded-lg lg:rounded-none lg:overflow-y-auto lg:[&::-webkit-scrollbar]:w-2 lg:[&::-webkit-scrollbar-track]:bg-ui-background-tertiary lg:[&::-webkit-scrollbar-track]:dark:bg-ui-background-tertiary lg:[&::-webkit-scrollbar-thumb]:bg-neutral-300 lg:[&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 lg:[&::-webkit-scrollbar-thumb]:rounded-full lg:[&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 lg:[&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
                  <nav className="p-4">
                    {sections.map((section) => {
                      const Icon = section.icon;
                      return (
                        <div
                          key={section.id}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg mb-2 text-ui-text-tertiary"
                        >
                          <Icon size={18} />
                          {section.label}
                        </div>
                      );
                    })}
                  </nav>
                </div>
                {/* Content area with loading */}
                <div className="flex-1 bg-ui-background-secondary lg:bg-white lg:dark:bg-ui-background-secondary lg:overflow-y-auto lg:[&::-webkit-scrollbar]:w-2 lg:[&::-webkit-scrollbar-track]:bg-ui-background-tertiary lg:[&::-webkit-scrollbar-track]:dark:bg-ui-background-tertiary lg:[&::-webkit-scrollbar-thumb]:bg-neutral-300 lg:[&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 lg:[&::-webkit-scrollbar-thumb]:rounded-full lg:[&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 lg:[&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
                  <div className="bg-ui-background-secondary rounded-lg lg:rounded-none lg:bg-white lg:dark:bg-ui-background-secondary h-full flex items-center justify-center">
                    <Spinner size={48} />
                  </div>
                </div>
              </>
            ) : error && !workspace ? (
              <>
                {/* Sidebar placeholder during error */}
                <div className="lg:w-64 flex-shrink-0 lg:border-r border-b lg:border-b-0 border-ui-border-primary bg-ui-background-tertiary dark:bg-neutral-800/50 rounded-lg lg:rounded-none lg:overflow-y-auto lg:[&::-webkit-scrollbar]:w-2 lg:[&::-webkit-scrollbar-track]:bg-ui-background-tertiary lg:[&::-webkit-scrollbar-track]:dark:bg-ui-background-tertiary lg:[&::-webkit-scrollbar-thumb]:bg-neutral-300 lg:[&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 lg:[&::-webkit-scrollbar-thumb]:rounded-full lg:[&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 lg:[&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
                  <nav className="p-4">
                    {sections.map((section) => {
                      const Icon = section.icon;
                      return (
                        <div
                          key={section.id}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg mb-2 text-ui-text-tertiary"
                        >
                          <Icon size={18} />
                          {section.label}
                        </div>
                      );
                    })}
                  </nav>
                </div>
                {/* Content area with error */}
                <div className="flex-1 bg-ui-background-secondary lg:bg-white lg:dark:bg-ui-background-secondary lg:overflow-y-auto lg:[&::-webkit-scrollbar]:w-2 lg:[&::-webkit-scrollbar-track]:bg-ui-background-tertiary lg:[&::-webkit-scrollbar-track]:dark:bg-ui-background-tertiary lg:[&::-webkit-scrollbar-thumb]:bg-neutral-300 lg:[&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 lg:[&::-webkit-scrollbar-thumb]:rounded-full lg:[&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 lg:[&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
                  <div className="bg-ui-background-secondary rounded-lg lg:rounded-none lg:bg-white lg:dark:bg-ui-background-secondary h-full flex items-center justify-center">
                    <div className="text-red-500">Error: {error}</div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Sidebar Navigation */}
                <div className="lg:w-64 flex-shrink-0 lg:border-r border-b lg:border-b-0 border-ui-border-primary bg-ui-background-tertiary dark:bg-neutral-800/50 rounded-lg lg:rounded-none lg:overflow-y-auto lg:[&::-webkit-scrollbar]:w-2 lg:[&::-webkit-scrollbar-track]:bg-ui-background-tertiary lg:[&::-webkit-scrollbar-track]:dark:bg-ui-background-tertiary lg:[&::-webkit-scrollbar-thumb]:bg-neutral-300 lg:[&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 lg:[&::-webkit-scrollbar-thumb]:rounded-full lg:[&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 lg:[&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
                  <nav className="p-4">
                    {sections.map((section) => {
                      const Icon = section.icon;
                      const isActive = section.id === currentSection;

                      return (
                        <button
                          key={section.id}
                          onClick={() => handleSectionChange(section.id)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 mb-2 ${isActive
                            ? 'bg-brand-primary text-black shadow-sm'
                            : 'text-ui-text-secondary hover:text-ui-text-primary hover:bg-ui-background-tertiary'
                            }`}
                          data-tour={`settings-${section.id}`}
                        >
                          <Icon size={18} />
                          {section.label}
                        </button>
                      );
                    })}
                  </nav>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 bg-ui-background-secondary lg:bg-white lg:dark:bg-ui-background-secondary lg:overflow-y-auto lg:[&::-webkit-scrollbar]:w-2 lg:[&::-webkit-scrollbar-track]:bg-ui-background-tertiary lg:[&::-webkit-scrollbar-track]:dark:bg-ui-background-tertiary lg:[&::-webkit-scrollbar-thumb]:bg-neutral-300 lg:[&::-webkit-scrollbar-thumb]:dark:bg-neutral-600 lg:[&::-webkit-scrollbar-thumb]:rounded-full lg:[&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 lg:[&::-webkit-scrollbar-thumb:hover]:dark:bg-neutral-500">
                  <div className="bg-ui-background-secondary rounded-lg lg:rounded-none lg:bg-white lg:dark:bg-ui-background-secondary p-4 lg:p-6 h-full">
                    <CurrentSectionComponent workspace={workspace} workspaceId={workspaceId} api={api} />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <HelpSystem pageName="projectSettings" />
    </div>
  );
};

export default ProjectSettingsModal;